import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface AgentFeedbackRequest {
  agentName: string;
  stars?: number;
  projectId?: string;
  organizationId?: string;
  minutesSaved?: number;
  comment?: string;
  isFirstTime?: boolean;
}

export interface AgentFeedbackResponse {
  id: string;
  userId: string;
  agentName: string;
  projectId?: string;
  organizationId?: string;
  stars: number;
  minutesSaved?: number;
  comment?: string;
  createdAt: string;
  isFirstTime?: boolean;
}

/**
 * Configuración de estrellas: mapea estrellas a minutos ahorrados estimados
 */
export const STAR_RATING_MINUTES: { [key: number]: number } = {
  1: 0,    // 1 estrella = 0 minutos
  2: 45,    // 2 estrellas = 45 minutos (Rango 30-60 min)
  3: 75,    // 3 estrellas = 1 hora 15 minutos (Rango 1h - 1h 30min)
  4: 105,   // 4 estrellas = 1 hora 45 minutos (Rango 1h 30min - 2h)
  5: 150    // 5 estrellas = 2 horas 30 minutos (Rango +2h)
};

export const STAR_RATING_LABELS: { [key: number]: string } = {
  1: '0 minutos',
  2: '30 minutos - 1 hora',
  3: '1 hora - 1 hora 30 minutos',
  4: '1 hora 30 minutos - 2 horas',
  5: '+ 2 horas'
};

@Injectable({
  providedIn: 'root'
})
export class FeedbackService {

  private readonly api: string;

  constructor(private http: HttpClient) {
    this.api = `${environment.genaiApiUrl}`;
  }

  /**
   * Envía el feedback de un agente
   */
  submitFeedback(request: AgentFeedbackRequest): Observable<AgentFeedbackResponse> {
    // Auto-calcular minutos si no se proporciona
    if (!request.minutesSaved && request.stars) {
      request.minutesSaved = STAR_RATING_MINUTES[request.stars] || 0;
    }
    return this.http.post<AgentFeedbackResponse>(`${this.api}/feedback`, request);
  }

  /**
   * Obtiene el feedback de un agente específico
   */
  getFeedbackByAgent(agentName: string): Observable<AgentFeedbackResponse[]> {
    return this.http.get<AgentFeedbackResponse[]>(`${this.api}/feedback/agent/${agentName}`);
  }

  /**
   * Obtiene el feedback del usuario actual
   */
  getFeedbackByCurrentUser(): Observable<AgentFeedbackResponse[]> {
    return this.http.get<AgentFeedbackResponse[]>(`${this.api}/feedback/user`);
  }

  /**
   * Obtiene el feedback del usuario actual para un agente específico
   */
  getFeedbackByCurrentUserAndAgent(agentName: string): Observable<AgentFeedbackResponse[]> {
    return this.http.get<AgentFeedbackResponse[]>(`${this.api}/feedback/user/agent/${agentName}`);
  }

  /**
   * Obtiene la etiqueta de tiempo ahorrado según las estrellas
   */
  getMinutesSavedLabel(stars: number): string {
    return STAR_RATING_LABELS[stars] || '';
  }
}
